import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DashboardHeader from '../components/DashboardHeader';
import TermsModal from '../components/TermsModal';
import './ProfilePage.css';
import '../styles/pageAnimation.css';

function ProfilePage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [rewards, setRewards] = useState(null);
  const [availableRewards, setAvailableRewards] = useState([]);
  const [rewardsIndex, setRewardsIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showGreenBar, setShowGreenBar] = useState(true);
  const [addressForm, setAddressForm] = useState({
    label: '',
    firstName: '',
    lastName: '',
    email: '',
    fullAddress: '',
    apartment: '',
    city: '',
    province: 'CAVITE',
    postalCode: '',
    phone: '',
    landmark: '',
    deliveryNotes: '',
    isDefault: false
  });
  const [addressErrors, setAddressErrors] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameForm, setNameForm] = useState({ firstName: '', lastName: '' });
  const [nameError, setNameError] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // Dynamic provinces: defaults merged with backend list
  // Provinces are sourced from Delivery Zones to avoid drift and duplicates
  const [provincesMap, setProvincesMap] = useState({});
  const [loadingProvinces, setLoadingProvinces] = useState(true);

  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const resp = await fetch(`${process.env.REACT_APP_API}/api/delivery/public-provinces`);
        if (!resp.ok) throw new Error('Failed provinces');
        const data = await resp.json();
        const result = {};
        for (const p of (Array.isArray(data) ? data : [])) {
          const name = (p.name || '').toString().trim();
          if (!name) continue;
          const cities = Array.isArray(p.cities) ? p.cities.filter(Boolean) : [];
          result[name] = Array.from(new Set(cities)).sort((a,b)=>a.localeCompare(b));
        }
        const sorted = Object.fromEntries(Object.keys(result).sort((a,b)=>a.localeCompare(b)).map(k => [k, result[k]]));
        setProvincesMap(sorted);
      } catch (e) {
        console.error('ProfilePage: provinces fetch failed', e);
        setProvincesMap({});
      } finally {
        setLoadingProvinces(false);
      }
    };
    fetchProvinces();
  }, []);

  // Handle responsive breakpoint
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Hide green bar on scroll (no animation, instant show/hide)
  useEffect(() => {
    const handleScroll = () => {
      setShowGreenBar(window.scrollY === 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    loadProfile();
    if (activeTab === 'rewards') {
      loadRewards();
      fetchAvailableRewards();
    }
  }, [activeTab]);

  const fetchAvailableRewards = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/api/rewards/public`);
      if (!response.ok) throw new Error('Failed to load rewards');
      const data = await response.json();
      setAvailableRewards(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching available rewards:', err);
      setAvailableRewards([]);
    }
  };

  const loadRewards = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/api/users/rewards`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('Failed to load rewards');
      const data = await response.json();
      setRewards(data);
    } catch (err) {
      console.error('Error loading rewards:', err);
    }
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API}/api/users/profile`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('Failed to load profile');
      const data = await response.json();
      setUser(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openAddressModal = (address = null) => {
    if (address) {
      setAddressForm({
        label: address.label,
        firstName: address.firstName,
        lastName: address.lastName,
        email: address.email || '',
        fullAddress: address.fullAddress,
        apartment: address.apartment || '',
        city: address.city,
        province: (address.province || '').toString().trim() || 'CAVITE',
        postalCode: address.postalCode,
        phone: address.phone,
        landmark: address.landmark || '',
        deliveryNotes: address.deliveryNotes || '',
        isDefault: address.isDefault
      });
      setEditingAddress(address._id);
    } else {
      setAddressForm({
        label: '',
        firstName: '',
        lastName: '',
        email: '',
        fullAddress: '',
        apartment: '',
        city: '',
        province: 'CAVITE',
        postalCode: '',
        phone: '',
        landmark: '',
        deliveryNotes: '',
        isDefault: user?.savedAddresses?.length === 0
      });
      setEditingAddress(null);
    }
    setShowAddressModal(true);
  };

  const handleAddressFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddressForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!addressForm.label || addressForm.label.trim().length < 2) errors.label = 'Please provide an address label (e.g., Home).';
    if (!addressForm.firstName || addressForm.firstName.trim() === '') errors.firstName = 'First name is required.';
    if (!addressForm.lastName || addressForm.lastName.trim() === '') errors.lastName = 'Last name is required.';
    if (!addressForm.fullAddress || addressForm.fullAddress.trim().length < 5) errors.fullAddress = 'Please enter a full address (block/lot and street).';
    if (!addressForm.apartment || addressForm.apartment.trim() === '') errors.apartment = 'Barangay / Apartment is required.';
    if (!addressForm.city || addressForm.city.trim() === '') errors.city = 'City is required.';
    if (!addressForm.postalCode || !/^\d{4}$/.test(addressForm.postalCode.trim())) errors.postalCode = 'Postal code must be 4 digits.';
    if (!addressForm.province || addressForm.province.trim() === '') errors.province = 'Province is required.';
    if (!addressForm.phone || !/^(?:\+63|0)9\d{9}$/.test(addressForm.phone.trim())) errors.phone = 'Phone must be a valid Philippine mobile number (e.g. 0927xxxxxxx).';
    setAddressErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const url = editingAddress 
        ? `${process.env.REACT_APP_API}/api/users/addresses/${editingAddress}`
        : `${process.env.REACT_APP_API}/api/users/addresses`;
      
      const method = editingAddress ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(addressForm)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save address');
      }

      await loadProfile();
      setShowAddressModal(false);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/api/users/addresses/${addressId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (!response.ok) throw new Error('Failed to delete address');
      await loadProfile();
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/api/users/addresses/${addressId}/set-default`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      if (!response.ok) throw new Error('Failed to set default address');
      await loadProfile();
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRewardsNext = () => {
    if (rewardsIndex < availableRewards.length - 1) {
      setRewardsIndex(rewardsIndex + 1);
    }
  };

  const handleRewardsPrev = () => {
    if (rewardsIndex > 0) {
      setRewardsIndex(rewardsIndex - 1);
    }
  };

  const handleEditName = () => {
    setNameForm({ firstName: user?.firstName || '', lastName: user?.lastName || '' });
    setIsEditingName(true);
    setNameError('');
  };

  const handleSaveName = async () => {
    if (!nameForm.firstName.trim() || !nameForm.lastName.trim()) {
      setNameError('Both first and last name are required');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API}/api/users/profile/name`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(nameForm)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update name');
      }

      await loadProfile();
      setIsEditingName(false);
      setNameError('');
    } catch (err) {
      setNameError(err.message);
    }
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setNameError('');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmNewPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API}/api/users/profile/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(passwordForm)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to change password');
      }

      setPasswordSuccess('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (err) {
      setPasswordError(err.message);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteConfirmEmail || deleteConfirmEmail.trim() === '') {
      setDeleteError('Please enter your email to confirm');
      return;
    }

    if (deleteConfirmEmail.trim() !== user?.email) {
      setDeleteError('Email does not match your account email');
      return;
    }

    if (!window.confirm('This action cannot be undone. Are you absolutely sure you want to delete your account?')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API}/api/users/account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ confirmEmail: deleteConfirmEmail.trim() })
      });

      if (!response.ok) {
        // Try to parse JSON error, fallback to text if it fails
        let errorMessage = 'Failed to delete account';
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch (e) {
          const text = await response.text();
          errorMessage = text || `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      // Clear local storage and redirect to home
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('isAdmin');
      alert('Your account has been deleted successfully.');
      window.location.href = '/';
    } catch (err) {
      console.error('Delete account error:', err);
      setDeleteError(err.message || 'An unexpected error occurred');
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3 }
    }
  };

  return (
    <div style={{ background: 'linear-gradient(180deg, #f8fdf9 0%, #f0f7f1 100%)', minHeight: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar (thinner) - only visible at top */}
      {showGreenBar && (
        <div style={{ width: '100%', background: 'linear-gradient(90deg, #175c36 0%, #1a6b3f 100%)', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }}>
          {/* Empty green bar for accent */}
        </div>
      )}
      {/* Navbar always sticky at top */}
      <div style={{ 
        width: '100%', 
        background: 'rgba(255, 255, 255, 0.98)', 
        backdropFilter: 'blur(10px)', 
        boxShadow: '0 4px 16px rgba(0,0,0,0.10)', 
        position: 'sticky',
        top: 0, 
        zIndex: 9998,
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
      }}>
        <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', background: 'transparent' }}>
          <DashboardHeader />
        </div>
      </div>
      <div style={{ width: '100%', flex: 1 }} className="page-content-fade-in">

      {/* Main Content */}
      <div style={{ width: '100%', maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '20px 16px' : '40px 24px' }}>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="profile-card"
        >
          {/* Header */}
          <div className="profile-header-row">
            <motion.h1 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="profile-title"
            >
              My Profile
            </motion.h1>
            
            {/* Tab Navigation */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="profile-nav modern-nav"
            >
              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`nav-tab ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => setActiveTab('profile')}
              >
                👤 Profile & Addresses
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`nav-tab ${activeTab === 'rewards' ? 'active' : ''}`}
                onClick={() => setActiveTab('rewards')}
              >
                🎁 Rewards & Points
              </motion.button>
            </motion.div>
          </div>

          {error && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="error-message"
            >
              {error}
            </motion.div>
          )}

          {loading ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ 
                padding: '60px 20px', 
                textAlign: 'center',
                background: '#fff',
                borderRadius: '16px',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                marginTop: '24px'
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1, rotate: 360 }}
                transition={{ duration: 0.6, rotate: { duration: 1, repeat: Infinity, ease: 'linear' } }}
                style={{ fontSize: '4rem', marginBottom: '20px' }}
              >
                🌱
              </motion.div>
              <p style={{ fontSize: '1.1rem', color: '#64748b', fontWeight: 500 }}>Loading your profile...</p>
            </motion.div>
          ) : (
            <AnimatePresence mode="wait">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <motion.div
                  key="profile-tab"
                  initial="hidden"
                  animate="visible"
                  exit={{ opacity: 0, y: -20 }}
                  variants={containerVariants}
                  className="profile-content-wrapper"
                >
                  {/* Left Column - Profile Info */}
                  <div className="profile-left-column">
                    <motion.div variants={itemVariants} className="profile-section">
                      <h2 className="section-title">Email</h2>
                      <input className="profile-input" type="email" value={user?.email || ''} disabled />
                    </motion.div>
                    
                    <motion.div variants={itemVariants} className="profile-section">
                      <h2 className="section-title">Full Name</h2>
                      {isEditingName ? (
                        <div>
                          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                            <input
                              className="profile-input"
                              type="text"
                              placeholder="First Name"
                              value={nameForm.firstName}
                              onChange={(e) => setNameForm({ ...nameForm, firstName: e.target.value })}
                            />
                            <input
                              className="profile-input"
                              type="text"
                              placeholder="Last Name"
                              value={nameForm.lastName}
                              onChange={(e) => setNameForm({ ...nameForm, lastName: e.target.value })}
                            />
                          </div>
                          {nameError && <div style={{ color: '#dc2626', fontSize: '0.9rem', marginBottom: '12px' }}>{nameError}</div>}
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <motion.button
                              onClick={handleSaveName}
                              style={{
                                background: '#15803d',
                                color: '#fff',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '6px',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                flex: 1
                              }}
                            >
                              Save
                            </motion.button>
                            <motion.button
                              onClick={handleCancelEditName}
                              style={{
                                background: '#f3f4f6',
                                color: '#1f2937',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '6px',
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                flex: 1
                              }}
                            >
                              Cancel
                            </motion.button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <input className="profile-input" type="text" value={`${user?.firstName || ''} ${user?.lastName || ''}`} disabled />
                          <motion.button
                            onClick={handleEditName}
                            style={{
                              background: '#15803d',
                              color: '#fff',
                              border: 'none',
                              padding: '12px 24px',
                              borderRadius: '8px',
                              fontSize: '0.95rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              width: '100%',
                              marginTop: '12px'
                            }}
                          >
                            Edit Name
                          </motion.button>
                        </>
                      )}
                    </motion.div>

                    <motion.div variants={itemVariants} className="profile-section">
                      <h2 className="section-title">Password</h2>
                      <motion.button
                        onClick={() => setShowPasswordModal(true)}
                        style={{
                          background: '#15803d',
                          color: '#fff',
                          border: 'none',
                          padding: '12px 24px',
                          borderRadius: '8px',
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          width: '100%'
                        }}
                      >
                        Change Password
                      </motion.button>
                    </motion.div>
                  </div>

                  {/* Right Column - Addresses */}
                  <motion.div variants={itemVariants} className="profile-right-column">
                    <div className="profile-section">
                      <div className="section-header">
                        <h2 className="section-title">
                          Delivery Addresses <span className="address-count">({user?.savedAddresses?.length || 0}/3)</span>
                        </h2>
                        {(!user?.savedAddresses || user.savedAddresses.length < 3) && (
                          <motion.button 
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="add-btn" 
                            onClick={() => openAddressModal()}
                          >
                            + Add Address
                          </motion.button>
                        )}
                      </div>

                      {!user?.savedAddresses?.length ? (
                        <p className="no-addresses">No saved addresses. Add one to speed up checkout!</p>
                      ) : (
                        <div className="addresses-list">
                          {user.savedAddresses.map((address, index) => (
                            <motion.div 
                              key={address._id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                              whileHover={{ y: -4 }}
                              className={`address-card ${address.isDefault ? 'default' : ''}`}
                            >
                              <div className="address-header">
                                <h3>{address.label}</h3>
                                {address.isDefault && <span className="default-badge">Default</span>}
                              </div>
                              <div className="address-details">
                                <p><strong>{address.firstName} {address.lastName}</strong></p>
                                <p>{address.fullAddress}</p>
                                {address.apartment && <p>{address.apartment}</p>}
                                <p>{address.city}, {address.province} {address.postalCode}</p>
                                <p>Phone: {address.phone}</p>
                              </div>
                              <div className="address-actions">
                                <motion.button 
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() => openAddressModal(address)}
                                >
                                  Edit
                                </motion.button>
                                {!address.isDefault && (
                                  <motion.button 
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handleSetDefault(address._id)}
                                  >
                                    Set as Default
                                  </motion.button>
                                )}
                                <motion.button 
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  className="delete-btn" 
                                  onClick={() => handleDeleteAddress(address._id)}
                                >
                                  Delete
                                </motion.button>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>

                  {/* Danger Zone - Delete Account */}
                  <motion.div variants={itemVariants} className="profile-full-width">
                    <div className="profile-section danger-section">
                      <h2 className="section-title" style={{ color: '#dc2626' }}>⚠️ Danger Zone</h2>
                      <p style={{ color: '#64748b', marginBottom: '16px', fontSize: '0.95rem' }}>
                        Deleting your account is permanent and cannot be undone. All your data, including orders, addresses, and reward points will be lost.
                      </p>
                      <motion.button
                        className="delete-account-btn"
                        onClick={() => setShowDeleteModal(true)}
                        style={{
                          background: '#dc2626',
                          color: '#fff',
                          border: 'none',
                          padding: '12px 24px',
                          borderRadius: '8px',
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        Delete My Account
                      </motion.button>
                    </div>
                  </motion.div>
                </motion.div>
              )}

              {/* Rewards Tab */}
              {activeTab === 'rewards' && (
                <motion.div
                  key="rewards-tab"
                  initial="hidden"
                  animate="visible"
                  exit={{ opacity: 0, y: -20 }}
                  variants={containerVariants}
                  className="rewards-section"
                >
                  {/* Left Section */}
                  <div className="rewards-left-section">
                    <motion.div variants={itemVariants} className="points-card">
                      <h2>🎁 Your Points</h2>
                      <div className="points-balance">
                        <span className="points-number">{rewards?.points || 0}</span>
                        <span className="points-label">Available Points</span>
                      </div>
                      <p className="total-earned">Total Earned: {rewards?.totalPointsEarned || 0} points</p>
                    </motion.div>

                    <motion.div variants={itemVariants} className="rewards-carousel">
                      {availableRewards.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No rewards available.</div>
                      ) : isMobile ? (
                        <div className="rewards-mobile-list">
                          {availableRewards.map((r, idx) => (
                            <motion.div 
                              key={r._id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: idx * 0.1 }}
                              className="reward-item"
                            >
                              <div className="reward-icon">{r.type === 'delivery' ? '🚚' : '💰'}</div>
                              <h3>{r.name}</h3>
                              <p>{r.description}</p>
                              <p className="reward-value">
                                {r.valueType === 'percent' ? `${r.value}%` : `₱${r.value}`} {r.type === 'delivery' ? 'off delivery' : 'off cart'}
                              </p>
                              {r.minCartAmount > 0 && <p style={{ fontSize: '0.85rem', color: '#64748b' }}>Min Cart: ₱{r.minCartAmount}</p>}
                              {r.expirationDate && <p style={{ fontSize: '0.85rem', color: '#64748b' }}>Expires: {new Date(r.expirationDate).toLocaleDateString()}</p>}
                              <div style={{ marginTop: 12 }}>
                                {((!rewards) || ((rewards.points || 0) < (r.pointCost || 0))) ? (
                                  <span style={{ color: '#dc2626', fontSize: '0.9rem', fontWeight: 600 }}>Insufficient Points</span>
                                ) : (
                                  <span style={{ color: '#15803d', fontSize: '0.9rem', fontWeight: 600 }}>Redeem at checkout</span>
                                )}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      ) : (
                        <div className="rewards-carousel-wrapper">
                          {availableRewards.length > 1 && (
                            <motion.button 
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="carousel-btn prev" 
                              onClick={handleRewardsPrev}
                              disabled={rewardsIndex === 0}
                            >
                              ‹
                            </motion.button>
                          )}
                          <div className="rewards-carousel-container">
                            <div 
                              className="rewards-track" 
                              style={{ 
                                display: 'flex', 
                                transform: `translateX(-${rewardsIndex * 100}%)`,
                                transition: 'transform 420ms cubic-bezier(.22,.9,.37,1)',
                              }}
                            >
                              {availableRewards.map((r, idx) => (
                                <motion.div 
                                  key={r._id}
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: 0.2 }}
                                  className="reward-item"
                                >
                                  <div className="reward-icon">{r.type === 'delivery' ? '🚚' : '💰'}</div>
                                  <h3>{r.name}</h3>
                                  <p>{r.description}</p>
                                  <p className="reward-value">
                                    {r.valueType === 'percent' ? `${r.value}%` : `₱${r.value}`} {r.type === 'delivery' ? 'off delivery' : 'off cart'}
                                  </p>
                                  {r.minCartAmount > 0 && <p style={{ fontSize: '0.85rem', color: '#64748b' }}>Min Cart: ₱{r.minCartAmount}</p>}
                                  {r.expirationDate && <p style={{ fontSize: '0.85rem', color: '#64748b' }}>Expires: {new Date(r.expirationDate).toLocaleDateString()}</p>}
                                  <div style={{ marginTop: 12 }}>
                                    {((!rewards) || ((rewards.points || 0) < (r.pointCost || 0))) ? (
                                      <span style={{ color: '#dc2626', fontSize: '0.9rem', fontWeight: 600 }}>Insufficient Points</span>
                                    ) : (
                                      <span style={{ color: '#15803d', fontSize: '0.9rem', fontWeight: 600 }}>Redeem at checkout</span>
                                    )}
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                          </div>
                          {availableRewards.length > 1 && (
                            <motion.button 
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="carousel-btn next" 
                              onClick={handleRewardsNext}
                              disabled={rewardsIndex >= availableRewards.length - 1}
                            >
                              ›
                            </motion.button>
                          )}
                        </div>
                      )}
                    </motion.div>
                  </div>

                  {/* Right Section */}
                  <div className="rewards-right-section">
                    <motion.div variants={itemVariants} className="earn-points-info">
                      <h3>How to Earn Points</h3>
                      <ul>
                        <li>🛍️ <strong>+1 point</strong> for every ₱10 spent</li>
                        <li>🎊 <strong>+100 bonus points</strong> on first purchase</li>
                      </ul>
                    </motion.div>

                    {rewards?.pointsHistory && rewards.pointsHistory.length > 0 && (
                      <motion.div variants={itemVariants} className="points-history">
                        <h3>Points History</h3>
                        <div className="history-list">
                          {[...rewards.pointsHistory]
                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                            .slice(0, 10)
                            .map((entry, idx) => {
                              let displayReason = entry.reason;
                              const orderMatch = entry.reason.match(/Order #([a-f0-9]+):/);
                              if (orderMatch && orderMatch[1]) {
                                const orderId = orderMatch[1];
                                displayReason = entry.reason.replace(orderMatch[1], orderId.slice(-8));
                              }
                              return (
                                <motion.div 
                                  key={idx}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: idx * 0.05 }}
                                  className="history-item"
                                >
                                  <div className="history-info">
                                    <span className="history-reason">{displayReason}</span>
                                    <span className="history-date">{new Date(entry.date).toLocaleDateString()}</span>
                                  </div>
                                  <span className={`history-points ${entry.amount > 0 ? 'earned' : 'redeemed'}`}>
                                    {entry.amount > 0 ? '+' : ''}{entry.amount} pts
                                  </span>
                                </motion.div>
                              );
                            })}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </motion.div>
      </div>

      {/* Address Modal */}
      <AnimatePresence>
        {showAddressModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay" 
            onClick={() => setShowAddressModal(false)}
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="modal-content" 
              onClick={e => e.stopPropagation()}
            >
              <h2>{editingAddress ? 'Edit Address' : 'Add New Address'}</h2>
              <form onSubmit={handleSaveAddress}>
                <div>
                  <label>
                    Address Label (e.g., "Home", "Office"):
                    <input
                      type="text"
                      name="label"
                      value={addressForm.label}
                      onChange={handleAddressFormChange}
                      placeholder="Home"
                      required
                    />
                    {addressErrors.label && <div style={{ color: 'red', fontSize: '0.9rem', marginTop: 6 }}>{addressErrors.label}</div>}
                  </label>

                  <div className="form-row">
                    <label>
                      First Name:
                      <input
                        type="text"
                        name="firstName"
                        value={addressForm.firstName}
                        onChange={handleAddressFormChange}
                        required
                      />
                      {addressErrors.firstName && <div style={{ color: 'red', fontSize: '0.9rem', marginTop: 6 }}>{addressErrors.firstName}</div>}
                    </label>
                    <label>
                      Last Name:
                      <input
                        type="text"
                        name="lastName"
                        value={addressForm.lastName}
                        onChange={handleAddressFormChange}
                        required
                      />
                      {addressErrors.lastName && <div style={{ color: 'red', fontSize: '0.9rem', marginTop: 6 }}>{addressErrors.lastName}</div>}
                    </label>
                  </div>

                  <label>
                    Email:
                    <input
                      type="email"
                      name="email"
                      value={addressForm.email}
                      onChange={handleAddressFormChange}
                      placeholder="your.email@example.com"
                      required
                    />
                  </label>

                  <label>
                    Full Address (Block and Lot#):
                    <input
                      type="text"
                      name="fullAddress"
                      value={addressForm.fullAddress}
                      onChange={handleAddressFormChange}
                      required
                    />
                    {addressErrors.fullAddress && <div style={{ color: 'red', fontSize: '0.9rem', marginTop: 6 }}>{addressErrors.fullAddress}</div>}
                  </label>

                  <label>
                    Barangay / Apartment, Suite, etc:
                    <input
                      type="text"
                      name="apartment"
                      value={addressForm.apartment}
                      onChange={handleAddressFormChange}
                    />
                    {addressErrors.apartment && <div style={{ color: 'red', fontSize: '0.9rem', marginTop: 6 }}>{addressErrors.apartment}</div>}
                  </label>

                  <div className="form-row">
                    <label>
                      Postal Code:
                      <input
                        type="text"
                        name="postalCode"
                        value={addressForm.postalCode}
                        onChange={handleAddressFormChange}
                        required
                      />
                      {addressErrors.postalCode && <div style={{ color: 'red', fontSize: '0.9rem', marginTop: 6 }}>{addressErrors.postalCode}</div>}
                    </label>
                  </div>

                  <label>
                    Province:
                    <select
                      name="province"
                      value={addressForm.province}
                      onChange={(e) => {
                        const newProvince = e.target.value;
                        const cities = provincesMap[newProvince] || [];
                        setAddressForm(prev => ({ ...prev, province: newProvince, city: cities[0] || '' }));
                      }}
                      required
                    >
                      <option value="">{loadingProvinces ? 'Loading provinces...' : 'Select a province'}</option>
                      {Object.keys(provincesMap).map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    {addressErrors.province && <div style={{ color: 'red', fontSize: '0.9rem', marginTop: 6 }}>{addressErrors.province}</div>}
                  </label>

                  <label>
                    City:
                    <select name="city" value={addressForm.city} onChange={handleAddressFormChange} required disabled={!addressForm.province}>
                      {addressForm.province ? ((provincesMap[addressForm.province] || []).map(c => <option key={c} value={c}>{c}</option>)) : <option value="">Select province first</option>}
                    </select>
                    {addressErrors.city && <div style={{ color: 'red', fontSize: '0.9rem', marginTop: 6 }}>{addressErrors.city}</div>}
                  </label>

                  <label>
                    Phone:
                    <input
                      type="tel"
                      name="phone"
                      value={addressForm.phone}
                      onChange={handleAddressFormChange}
                      required
                    />
                    {addressErrors.phone && <div style={{ color: 'red', fontSize: '0.9rem', marginTop: 6 }}>{addressErrors.phone}</div>}
                  </label>

                  <label>
                    Landmark (Optional - e.g., "Near Jollibee", "Beside City Hall"):
                    <input
                      type="text"
                      name="landmark"
                      value={addressForm.landmark}
                      onChange={handleAddressFormChange}
                      placeholder="e.g., Near 7-Eleven"
                    />
                  </label>

                  <label>
                    Delivery Notes (Optional - instructions for the rider):
                    <textarea
                      name="deliveryNotes"
                      value={addressForm.deliveryNotes}
                      onChange={handleAddressFormChange}
                      placeholder="e.g., Gate code: 1234, Call upon arrival"
                      rows="3"
                      style={{ resize: 'vertical', fontFamily: 'inherit', padding: '12px', fontSize: '14px' }}
                    />
                  </label>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isDefault"
                      checked={addressForm.isDefault}
                      onChange={handleAddressFormChange}
                    />
                    Set as default address
                  </label>
                </div>

                <div className="modal-actions">
                  <motion.button 
                    type="submit"
                  >
                    {editingAddress ? 'Update Address' : 'Save Address'}
                  </motion.button>
                  <motion.button 
                    type="button" 
                    onClick={() => setShowAddressModal(false)}
                  >
                    Cancel
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Account Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteConfirmEmail('');
              setDeleteError('');
            }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="modal-content"
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: '550px', padding: '32px' }}
            >
              <h2 style={{ color: '#dc2626', marginBottom: '20px', fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>⚠️</span> Delete Account
              </h2>
              <p style={{ marginBottom: '16px', color: '#475569', lineHeight: 1.6, fontSize: '0.95rem' }}>
                This action is <strong style={{ color: '#dc2626' }}>permanent and cannot be undone</strong>. All your data will be deleted, including:
              </p>
              <ul style={{ marginBottom: '24px', marginLeft: '24px', color: '#64748b', lineHeight: 2, fontSize: '0.9rem' }}>
                <li>Order history</li>
                <li>Saved addresses</li>
                <li>Reward points ({user?.points || 0} points)</li>
                <li>Active vouchers</li>
                <li>Account information</li>
              </ul>
              <div style={{ 
                background: '#fef3c7', 
                border: '1px solid #fcd34d',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '20px'
              }}>
                <p style={{ margin: 0, color: '#92400e', fontWeight: 600, fontSize: '0.9rem' }}>
                  To confirm deletion, please type your email address:
                </p>
                <p style={{ margin: '4px 0 0 0', color: '#15803d', fontWeight: 700, fontSize: '0.95rem' }}>
                  {user?.email}
                </p>
              </div>
              <input
                type="email"
                value={deleteConfirmEmail}
                onChange={(e) => {
                  setDeleteConfirmEmail(e.target.value);
                  setDeleteError('');
                }}
                placeholder="Enter your email to confirm"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  fontSize: '0.95rem',
                  border: deleteError ? '2px solid #dc2626' : '2px solid #e5e7eb',
                  borderRadius: '8px',
                  marginBottom: deleteError ? '12px' : '24px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  if (!deleteError) {
                    e.target.style.borderColor = '#15803d';
                    e.target.style.boxShadow = '0 0 0 3px rgba(21, 128, 61, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  if (!deleteError) {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              />
              {deleteError && (
                <div style={{ 
                  color: '#dc2626', 
                  fontSize: '0.875rem', 
                  marginBottom: '20px', 
                  fontWeight: 500,
                  background: '#fee2e2',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #fecaca'
                }}>
                  {deleteError}
                </div>
              )}
              <div className="modal-actions" style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmEmail('');
                    setDeleteError('');
                  }}
                  style={{
                    background: 'white',
                    color: '#475569',
                    border: '2px solid #e5e7eb',
                    padding: '12px 28px',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleDeleteAccount}
                  style={{
                    background: '#dc2626',
                    color: '#fff',
                    border: '2px solid #dc2626',
                    padding: '12px 28px',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Yes, Delete My Account
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Change Password Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={() => {
              setShowPasswordModal(false);
              setPasswordForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
              setPasswordError('');
              setPasswordSuccess('');
            }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="modal-content"
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: '500px' }}
            >
              <h2 style={{ marginBottom: '20px' }}>🔒 Change Password</h2>
              <form onSubmit={handleChangePassword}>
                <label>
                  Current Password:
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    placeholder="Enter current password"
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '14px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      outline: 'none'
                    }}
                  />
                </label>
                <label>
                  New Password:
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    placeholder="Enter new password (min 6 characters)"
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '14px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      outline: 'none'
                    }}
                  />
                </label>
                <label>
                  Confirm New Password:
                  <input
                    type="password"
                    value={passwordForm.confirmNewPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmNewPassword: e.target.value })}
                    placeholder="Confirm new password"
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '14px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      outline: 'none'
                    }}
                  />
                </label>
                {passwordError && (
                  <div style={{ color: '#dc2626', fontSize: '0.9rem', marginBottom: '16px', fontWeight: 500 }}>
                    {passwordError}
                  </div>
                )}
                {passwordSuccess && (
                  <div style={{ color: '#15803d', fontSize: '0.9rem', marginBottom: '16px', fontWeight: 500 }}>
                    {passwordSuccess}
                  </div>
                )}
                <div className="modal-actions" style={{ marginTop: '24px' }}>
                  <motion.button
                    type="submit"
                    style={{
                      background: '#15803d',
                      color: '#fff',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Change Password
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
                      setPasswordError('');
                      setPasswordSuccess('');
                    }}
                    style={{
                      background: '#f3f4f6',
                      color: '#1f2937',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer style={{
        width: '100%',
        background: 'linear-gradient(135deg, #0f4527 0%, #145a34 50%, #0f4527 100%)',
        marginTop: 'auto',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.25)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.03) 0%, transparent 70%)',
          pointerEvents: 'none'
        }} />

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '40px 20px 20px',
          position: 'relative',
          zIndex: 1
        }}>
          {/* Main Footer Content */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '40px',
            alignItems: 'start',
            marginBottom: '35px'
          }}>
            {/* Connect Section - Hidden on mobile */}
            {!isMobile && <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                margin: 0,
                marginBottom: '4px'
              }}>Connect With Us</h3>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <a
                  href="https://www.facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a
                  href="https://m.me/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Messenger"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.875 1.44 5.449 3.687 7.126V22l3.486-1.914c.93.257 1.913.395 2.827.395 5.523 0 10-4.145 10-9.243S17.523 2 12 2zm.993 12.437l-2.552-2.725-4.983 2.725 5.48-5.813 2.613 2.725 4.922-2.725-5.48 5.813z"/>
                  </svg>
                </a>
                <a
                  href="viber://chat?number="
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Chat"
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    border: '1.5px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                    <path d="M11.4 0C9.473.028 5.333.344 3.02 2.467 1.302 4.187.696 6.7.633 9.817.57 12.933.488 18.776 6.12 20.36h.003l-.004 2.644s-.037.977.61 1.177c.777.242 1.234-.5 1.977-1.303.41-.44.972-1.084 1.397-1.58 3.85.326 6.812-.417 7.15-.526.776-.253 5.176-.816 5.892-6.657.74-6.02-.36-9.83-2.34-11.546-.596-.55-3.006-2.3-8.375-2.323 0 0-.395-.025-1.026-.017z"/>
                  </svg>
                </a>
              </div>
            </div>}

            {/* Logo Section */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              justifySelf: 'center',
              marginBottom: isMobile ? '10px' : '0'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2.5px solid rgba(255, 255, 255, 0.25)',
                padding: '10px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.1)',
                position: 'relative'
              }}>
                <img
                  src="/images/chiqui_logo_white-outline.svg"
                  alt="Chiqui's Garden Logo"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                  }}
                />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  color: '#ffffff',
                  fontSize: '1rem',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  marginBottom: '4px',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}>Chiqui's Garden</div>
                <div style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.75rem',
                  fontWeight: 400
                }}>© {new Date().getFullYear()} All Rights Reserved</div>
              </div>
            </div>

            {/* Location Section - Hidden on mobile */}
            {!isMobile && <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              justifySelf: 'end'
            }}>
              <h3 style={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                margin: 0,
                marginBottom: '4px'
              }}>Our Location</h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                padding: '14px 18px',
                borderRadius: '16px',
                border: '1.5px solid rgba(255, 255, 255, 0.15)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="rgba(255, 255, 255, 0.95)">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                </div>
                <div>
                  <div style={{
                    color: '#ffffff',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    marginBottom: '3px',
                    letterSpacing: '0.3px'
                  }}>Iba Garden</div>
                  <div style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.8rem',
                    lineHeight: '1.3'
                  }}>Pulong Iba, Silang, Cavite</div>
                </div>
              </div>
            </div>}
          </div>

          {/* Divider */}
          <div style={{
            width: '100%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
            margin: isMobile ? '20px 0' : '25px 0'
          }} />

          {/* Bottom Links */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: isMobile ? '8px' : '30px',
            paddingBottom: '10px'
          }}>
            <a
              href="/about"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              About Us
            </a>
            <a
              href="/contact"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Contact Info
            </a>
            <a
              href="/return-policy"
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Return & Exchange Policy
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Terms of Use
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}
              style={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.5px',
                transition: 'all 0.3s ease',
                padding: isMobile ? '4px 8px' : '6px 12px',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#ffffff';
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
      </div>

      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />

      <TermsModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        isPrivacyPolicy={true}
      />

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default ProfilePage;
